import { useInfiniteQuery, useQuery } from 'react-query';
import api from 'src/services/api';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import { DEVICE_POLICY_QUERY_KEY } from 'src/constants/query';
import { objectToQueryString } from 'src/utils/uri';

const DEVICE_POLICY_DETAILS_QUERY_KEY = 'devicePolicyDetails';

const getDevicePolicies = async ({ customerEnvId, deviceId, page, limit, query = {}, signal }) =>
  api.get(
    `/environmentWdacPolicy/${customerEnvId}/device/${deviceId}?${objectToQueryString(query)}`,
    {
      params: {
        Page: page,
        Limit: limit,
      },
      ...(signal ? { signal } : {}),
    },
  );

const getDevicePolicyDetails = async ({ customerEnvId, deviceId, envPolicyVersionId, signal }) =>
  api.get(
    `/environmentWdacPolicy/${customerEnvId}/device/${deviceId}/policyVersion/${envPolicyVersionId}`,
    {
      ...(signal ? { signal } : {}),
    },
  );

const getDevicePolicyHistory = async ({
  customerEnvId,
  deviceId,
  envPolicyVersionId,
  page,
  limit,
  signal,
}) =>
  api.get(
    `/environmentWdacPolicy/${customerEnvId}/device/${deviceId}/policyVersion/${envPolicyVersionId}/history`,
    {
      params: {
        Page: page,
        Limit: limit,
      },
      ...(signal ? { signal } : {}),
    },
  );

export const useInfiniteDevicePolicies = ({ deviceId, apiQuery, limit = 50, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DEVICE_POLICY_QUERY_KEY, customerEnvId, deviceId, apiQuery, limit],
    ({ pageParam = 1, signal }) =>
      getDevicePolicies({
        customerEnvId,
        deviceId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!deviceId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.policies?.length || 0), 0);
        return loaded < (lastPage.count || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useDevicePolicyDetails = ({ deviceId, envPolicyVersionId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DEVICE_POLICY_DETAILS_QUERY_KEY, deviceId, customerEnvId, envPolicyVersionId],
    ({ signal }) => getDevicePolicyDetails({ customerEnvId, deviceId, envPolicyVersionId, signal }),
    {
      enabled: enabled && !!deviceId && !!customerEnvId && !!envPolicyVersionId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useInfiniteDevicePolicyHistory = ({
  deviceId,
  envPolicyVersionId,
  limit = 10,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [
      DEVICE_POLICY_DETAILS_QUERY_KEY,
      'history',
      deviceId,
      customerEnvId,
      envPolicyVersionId,
      limit,
    ],
    ({ pageParam = 1, signal }) =>
      getDevicePolicyHistory({
        customerEnvId,
        deviceId,
        envPolicyVersionId,
        page: pageParam,
        limit,
        signal,
      }),
    {
      enabled: enabled && !!deviceId && !!customerEnvId && !!envPolicyVersionId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.history?.length || 0), 0);
        return loaded < (lastPage.count || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};
